import { useEffect, useState } from 'react';
import { Edit2, Trash2, Search, Plus, UsersRound, UserCheck, UserX, ShieldCheck, Mail } from 'lucide-react';
import { CallAPIPostPromise } from 'Common/commaon';
import { useSelector } from 'react-redux';
import { selectToken } from 'slices/auth/login/reducer';
import { jwtDecode } from 'jwt-decode';
import { TwoButtonAlert } from 'Common/twoButtonAlert';
import { IoTrashOutline } from 'react-icons/io5';
import { toast } from 'react-toastify';
import { TeamManagementSkeleton } from 'components/shimmer';
import { hasPermission } from 'Common/permissions';
import { PERMISSIONS } from 'Common/constants/permissions';

interface User {
  id: string;
  user_first_name: string;
  user_last_name: string;
  user_email: string;
  role: 'Owner' | 'Staff';
  permissions: number;
  active: boolean;
  avatar?: string;
}

interface UserTableProps {
  onEditUser: (user: User) => void;
  onAddUser: () => void;
}

export function UserTable({ onEditUser, onAddUser }: UserTableProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [users, setUsers] = useState<User[]>([]);
  const [refresh,setRefresh]=useState(false)
  const [isLoading, setIsLoading] = useState(true);
  const [statusLoadingId, setStatusLoadingId] = useState<string | null>(null);
  const [deleteLoadingId, setDeleteLoadingId] = useState<string | null>(null);

  const [alertDetails,setAlertDetails]= useState({
    status: false,
    title: "Warning!",
    message: "You’re about to delete your account forever,",
    icon: IoTrashOutline,
    button1Text: "Keep it",
    button2Text: "Delete",
    button1Style: "px-4 py-2 rounded-lg bg-gray-100 text-black hover:bg-gray-200 transition",
    button2Style: "px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition",
    onButton1Click: () => console.log("Saved "),
    onButton2Click: () => console.log("Deleted "),
  })
  const getInitials = (user_first_name?: string, user_last_name?: string) => {
    const first = user_first_name?.[0] || '';
    const last = user_last_name?.[0] || '';
    
    return `${first}${last}`.toUpperCase();
  };
  const token = useSelector(selectToken)
  let decoded ="" as any
  if(token){
    decoded=jwtDecode(token) as any
  }
  const companyId = decoded.companyId;
  const toggleUserStatus = async (userId: string) => {
    if (statusLoadingId === userId) return;
    try {
      setStatusLoadingId(userId);
      // 1️⃣ Find the correct user
      const user = users.find((u: any) => u.id === userId) as any
      if (!user) return;
  
      // 2️⃣ Toggle status locally (optimistic update)
      const updatedStatus =
        user.status === "active" ? "inactive" : "active";
  
      setUsers((prev: any[]) =>
        prev.map((u: any) =>
          u.id === userId ? { ...u, status: updatedStatus } : u
        )
      );
  
      // 3️⃣ Call API with correct user data (NOT formData ❌)
      await CallAPIPostPromise(
        "/account/updateCompanyUser",
        {
          userId: user.id,
          companyId,
  
          // send existing values (since partial update API expects fields)
          firstName: user.user_first_name,
          lastName: user.user_last_name,
          email: user.user_email,
          gender: user.gender || "male",
          phone: user.phone,
          permissions: user.permissions,
  
          // 👇 include status if your backend supports it
          status: updatedStatus
        }
      ) as any;
      toast.success(`Team member marked ${updatedStatus}`);
  
    } catch (err) {
      console.error(err);
      toast.error("Unable to update team member status");
  
      // 4️⃣ Rollback UI if API fails
      setUsers((prev: any[]) =>
        prev.map((u: any) =>
          u.id === userId
            ? {
                ...u,
                status: u.status === "active" ? "inactive" : "active"
              }
            : u
        )
      );
    } finally {
      setStatusLoadingId(null);
    }
  };

  const filteredUsers = users.filter((user:any) => {
    const matchesSearch =
      user.user_first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.user_last_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.user_email.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && user.status==="active") ||
      (statusFilter === 'inactive' && user.status==="inactive");

    return matchesSearch && matchesStatus;
  });
  useEffect(()=>{
setIsLoading(true);
CallAPIPostPromise("/account/getCompanyUsers",{
  companyId
}).then((res:any)=>{
  setUsers(res?.data||[])
}).catch((error) => {
  console.error(error);
  toast.error("Unable to load team members");
}).finally(() => {
  setIsLoading(false);
})
  },[companyId, refresh])
  const canUpdateUser = hasPermission(PERMISSIONS.MANAGE_COMPANY_STAFF);
  const activeUsers = users.filter((user: any) => user.status === "active").length;
  const inactiveUsers = users.length - activeUsers;
  if (isLoading) {
    return <TeamManagementSkeleton />;
  }

  return (
    <div className="team-directory">
      <section className="team-stats" aria-label="Team summary">
        <article className="team-stat">
          <span className="team-stat__icon team-stat__icon--blue"><UsersRound size={20} /></span>
          <div><strong>{users.length}</strong><span>Total members</span></div>
        </article>
        <article className="team-stat">
          <span className="team-stat__icon team-stat__icon--green"><UserCheck size={20} /></span>
          <div><strong>{activeUsers}</strong><span>Active members</span></div>
        </article>
        <article className="team-stat">
          <span className="team-stat__icon team-stat__icon--orange"><UserX size={20} /></span>
          <div><strong>{inactiveUsers}</strong><span>Inactive members</span></div>
        </article>
      </section>

      <section className="team-panel">
      <div className="team-toolbar">
        <div className="team-toolbar__intro">
          <h2>Team directory</h2>
          <p>{filteredUsers.length} {filteredUsers.length === 1 ? "member" : "members"} shown</p>
        </div>
        <div className="team-toolbar__controls">
          <div className="team-search">
            <Search aria-hidden="true" />
            <input
              type="text"
              placeholder="Search by name or email"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search team members"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
            className="team-status-filter"
            aria-label="Filter by status"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        <button
          onClick={onAddUser}
          className="team-add-button"
        >
          <Plus size={17} />
          <span>Add member</span>
        </button>
      </div>
      </div>

      <div className="team-table-wrap">
        <div className="team-table-scroll">
          <table className="team-table">
            <thead>
              <tr>
                <th>Member</th>
                <th>Contact</th>
                <th>Role</th>
                <th>Access</th>
                <th>Status</th>
                {canUpdateUser && <th className="team-table__actions-heading">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user:any) => (
                <tr
                  key={user.id}
                  className={user.status !== "active" ? "team-member--inactive" : ""}
                >
                  <td>
                    <div className="team-member">
                      <div className="team-avatar">
                        {getInitials(user.user_first_name, user.user_last_name)}
                      </div>
                      <div><strong>{user.user_first_name} {user.user_last_name}</strong><span>Team member</span></div>
                    </div>
                  </td>
                  <td><span className="team-email"><Mail size={14} />{user.user_email}</span></td>
                  <td>
                    <span className={`team-role ${user.role === 'Owner' ? 'team-role--owner' : ''}`}>
                      {user.role}
                    </span>
                  </td>
                  <td>
                    <span className="team-permission-count"><ShieldCheck size={15} />
                      {Array.isArray(user.permissions) ? user.permissions.length : user.permissions || 0} permissions
                    </span>
                  </td>
                  <td>
  <label className="team-status-toggle" title={user.status === "active" ? "Deactivate member" : "Activate member"}>
    <input
      type="checkbox"
      checked={user.status==="active"}
      onChange={() => toggleUserStatus(user.id)}
      disabled={!canUpdateUser || statusLoadingId === user.id}
      aria-label={`${user.status === "active" ? "Deactivate" : "Activate"} ${user.user_first_name}`}
    />
    <span className="team-status-toggle__track"><span /></span>
    <small>{statusLoadingId === user.id ? "Updating" : user.status === "active" ? "Active" : "Inactive"}</small>
  </label>
</td>
{canUpdateUser &&   <td>
                    <div className="team-row-actions">
                      <button
                        onClick={() => onEditUser(user)}
                        disabled={deleteLoadingId === user.id}
                        className="team-action-button"
                        aria-label={`Edit ${user.user_first_name}`}
                        title="Edit member"
                      >
                        <Edit2 className="w-4 h-4 text-muted-foreground" />
                      </button>
                      <button
                      onClick={
                        ()=>{

                          setAlertDetails({
                            status: true,
                            title: "Warning!",
                            message: `You’re about to delete this team member account`,
                            icon: IoTrashOutline,
                            button1Text: "Close",
                            button2Text: "Delete Account",
                            button1Style: "px-4 py-2 rounded-lg bg-gray-100 text-black hover:bg-gray-200 transition",
                            button2Style: "px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition",
                            onButton1Click: () => console.log("Saved "),
                            onButton2Click: () => {
                              setDeleteLoadingId(user.id);
                             CallAPIPostPromise("/account/deleteCompanyUser",{
                              userId:user.id,
                               companyId
                             }).then(()=>{
                              toast.success("Team member deleted");
                             }).catch(()=>{
                              toast.error("Unable to delete team member");
                             }).finally(()=>{
                              setDeleteLoadingId(null);
                              setRefresh(r=>!r)
                             })

                            },
                          })
                        }
                      }
                        disabled={deleteLoadingId === user.id}
                        className="team-action-button team-action-button--danger"
                        aria-label={`Delete ${user.user_first_name}`}
                        title="Delete member"
                      >
                        {deleteLoadingId === user.id ? (
                          <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-red-200 border-t-red-500" />
                        ) : (
                          <Trash2 className="w-4 h-4 text-red-500" />
                        )}
                      </button>
                    </div>
                  </td>}
                
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr><td colSpan={canUpdateUser ? 6 : 5}>
                  <div className="team-empty-state">
                    <span><UsersRound size={25} /></span>
                    <strong>No team members found</strong>
                    <p>Try a different search or status filter.</p>
                  </div>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      </section>
      <TwoButtonAlert setDetails={setAlertDetails}  details={alertDetails}/>

    </div>
  );
}
